import { useState, type FormEvent } from 'react'
import type { OrderDetailResponse, PaymentKind, PaymentMethod, ShopifyOrderSummary } from '@fitting-room/contracts'
import { useRecordPayment } from '../../../lib/adminQueries'
import { dollarsFromMinor, minorFromDollars } from '../../../lib/appointments'
import { boutiqueDayOf, boutiqueToday } from '../../../lib/boutique'
import { formatCents } from '../../../lib/orders'
import { normalizeShopifyOrderNo } from '@fitting-room/contracts/order-math'
import {
  PAYMENT_KIND_LABEL,
  PAYMENT_KIND_ORDER,
  PAYMENT_METHOD_LABEL,
  PAYMENT_METHOD_ORDER,
  PAYMENT_METHOD_PHRASE,
  REFERENCE_LABEL,
  DEFAULT_PAYMENT_METHOD,
  defaultPaymentKind,
  shopifyOrderNoMethodNote,
  suggestedAmountMinor,
} from '../../../lib/payments'
import { useStaffToken } from '../../../lib/staffToken'
import { DatePicker } from '../DatePicker'
import { FormField, FormSheet } from '../FormSheet'
import { ShopifyOrderField } from './ShopifyOrderField'

/** One payment is at most $1,000,000, as the API holds it. */
const MAX_MINOR = 100_000_000

/**
 * Record a payment: tperent's `RecordPaymentDialog`, for a sale. What it was,
 * how, how much (the balance, to start with), the day the money moved, the
 * reference off the receipt and a note — and a sentence saying what is about
 * to be recorded, before it is.
 *
 * The Shopify order is a dropdown of the boutique's Shopify orders no
 * payment has yet (`ShopifyOrderField`); picking one fills the amount, the
 * day it was rung up and the reference, for the person to check and confirm.
 * The pencil beside it types the number instead, which always works,
 * Shopify connected or not.
 */
export function RecordPaymentDialog({
  detail,
  open,
  onClose,
}: {
  readonly detail: OrderDetailResponse
  readonly open: boolean
  readonly onClose: () => void
}) {
  return (
    <FormSheet
      open={open}
      onOpenChange={(next) => (next ? undefined : onClose())}
      title="Record payment"
      description={`${detail.order.orderNo} · ${detail.order.customerName}. Balance ${formatCents(Math.max(detail.money.balanceMinor, 0))}.`}
    >
      {open ? <RecordPaymentForm detail={detail} onDone={onClose} /> : null}
    </FormSheet>
  )
}

function RecordPaymentForm({ detail, onDone }: { readonly detail: OrderDetailResponse; readonly onDone: () => void }) {
  const { token } = useStaffToken()
  const record = useRecordPayment(token, detail.order.id)
  const canceled = detail.order.status === 'canceled'
  const today = boutiqueToday()

  const startKind: PaymentKind = canceled ? 'refund' : defaultPaymentKind(detail.money)
  const prefill = (kind: PaymentKind) => {
    const suggested = suggestedAmountMinor(kind, detail.money)
    return suggested === null ? '' : dollarsFromMinor(suggested)
  }
  const [kind, setKind] = useState<PaymentKind>(startKind)
  const [method, setMethod] = useState<PaymentMethod>(DEFAULT_PAYMENT_METHOD)
  const [amountText, setAmountText] = useState(() => prefill(startKind))
  const [amountTouched, setAmountTouched] = useState(false)
  const [paidAt, setPaidAt] = useState(today)
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [shopifyOrderNo, setShopifyOrderNo] = useState('')
  const [shopifyOrder, setShopifyOrder] = useState<ShopifyOrderSummary | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const amount = minorFromDollars(amountText)
  const amountProblem =
    amount === undefined || amount === null || amount <= 0
      ? 'An amount in dollars, like 500 or 1,250.50.'
      : amount > MAX_MINOR
        ? 'That’s over $1,000,000. Check the amount.'
        : null
  const backdated = paidAt < today

  // The suggestion belongs to the kind: a balance left in the field after
  // switching to a refund is a number somebody records without meaning to.
  const changeKind = (next: PaymentKind) => {
    setKind(next)
    if (!amountTouched) setAmountText(prefill(next))
  }
  const pick = (order: ShopifyOrderSummary | null) => {
    setShopifyOrder(order)
    if (!order) {
      setShopifyOrderNo('')
      return
    }
    setAmountText(dollarsFromMinor(order.totalMinor))
    setAmountTouched(true)
    // The day it was rung up, on the boutique's calendar — never ahead of today.
    const day = boutiqueDayOf(order.createdAt)
    setPaidAt(day < today ? day : today)
    setReference(order.name)
    // Shopify's name is the number on the receipt with its '#': stored without.
    setShopifyOrderNo(normalizeShopifyOrderNo(order.name) ?? '')
  }
  const type = (text: string) => {
    setShopifyOrder(null)
    setShopifyOrderNo(text)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setSubmitted(true)
    if (amountProblem || amount === undefined || amount === null) return
    record.mutate(
      {
        kind,
        method,
        amountMinor: amount,
        paidAt,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(method === 'shopify' && shopifyOrder ? { shopifyOrderId: shopifyOrder.id } : {}),
        ...(normalizeShopifyOrderNo(shopifyOrderNo) ? { shopifyOrderNo: normalizeShopifyOrderNo(shopifyOrderNo) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      { onSuccess: onDone },
    )
  }

  const kinds = canceled ? (['refund'] as const) : PAYMENT_KIND_ORDER
  return (
    <form onSubmit={submit} noValidate>
      {canceled ? (
        <p className="st-notice mb-3">This order is canceled, so it can only take a refund.</p>
      ) : null}
      <div className="grid grid-cols-2 gap-x-3">
        <FormField label="What it is">
          <select className="st-input" value={kind} onChange={(event) => changeKind(event.target.value as PaymentKind)}>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {PAYMENT_KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="How">
          <select className="st-input" value={method} onChange={(event) => setMethod(event.target.value as PaymentMethod)}>
            {PAYMENT_METHOD_ORDER.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABEL[m]}
              </option>
            ))}
          </select>
        </FormField>
      </div>

      <ShopifyOrderField picked={shopifyOrder} typed={shopifyOrderNo} onPick={pick} onType={type} note={shopifyOrderNoMethodNote(method, shopifyOrderNo)} />

      <div className="grid grid-cols-2 gap-x-3">
        <FormField label="Amount" error={submitted ? (amountProblem ?? undefined) : undefined}>
          <input
            className="st-input tabular-nums"
            inputMode="decimal"
            autoComplete="off"
            placeholder="0.00"
            value={amountText}
            onChange={(event) => {
              setAmountTouched(true)
              setAmountText(event.target.value)
            }}
          />
        </FormField>
        <div className="mb-3 flex flex-col gap-1">
          <span className="st-eyebrow">Paid on</span>
          <div className="flex items-center">
            <DatePicker value={paidAt} onChange={setPaidAt} label="Paid on" max={today} />
          </div>
          {backdated ? <span className="st-meta text-xs">Earlier than today, so it&rsquo;s marked back-dated.</span> : null}
        </div>
      </div>
      <FormField label={REFERENCE_LABEL[method]} hint={shopifyOrder ? `From Shopify: ${shopifyOrder.name}` : undefined}>
        <input className="st-input" maxLength={64} autoComplete="off" value={reference} onChange={(event) => setReference(event.target.value)} />
      </FormField>
      <FormField label="Note">
        <input className="st-input" maxLength={500} autoComplete="off" value={note} onChange={(event) => setNote(event.target.value)} />
      </FormField>

      {!amountProblem && amount ? (
        <p className="st-note mb-3 text-sm" data-testid="payment-confirm">
          {kind === 'refund'
            ? `Refunding ${formatCents(amount)} to the customer ${PAYMENT_METHOD_PHRASE[method]}.`
            : `Recording a ${PAYMENT_KIND_LABEL[kind].toLowerCase()} of ${formatCents(amount)} ${PAYMENT_METHOD_PHRASE[method]}.`}
        </p>
      ) : null}

      {record.isError ? (
        <p className="st-alert mb-3" role="alert">
          {record.error.message}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <button type="button" className="st-btn-ghost" onClick={onDone}>
          Not now
        </button>
        <button type="submit" className="st-btn" disabled={record.isPending}>
          {record.isPending ? 'Recording…' : kind === 'refund' ? 'Record refund' : 'Record payment'}
        </button>
      </div>
    </form>
  )
}
